import React, { useState, useEffect } from 'react'
import {DeleteOutlined, MoneyCollectOutlined} from '@ant-design/icons';
import PageHeading from '../../components/PageHeading';
import { debounce } from 'lodash';
import {
  Button,
  message,
  Table,
  Select,
  DatePicker,
  Tag,
  Popconfirm,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';
import fetchMiniList from '../../utils/miniFetcher.js';
import { dateFormatmicroSecond } from '../../utils/dateFormats';

const { RangePicker } = DatePicker;

const Bookings = () => {
  const [bookings, setBookings] = useState({
    results: []
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [movies, setMovies] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [times, setTimes] = useState([]);
  const [filters, setFilters] = useState({
    id: null,
    movie: null,
    theatre: null,
    timeslot: null,
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchMasters();
  }, []);

	useEffect(() => {
		const delayFetch = debounce(() => {
			fetchData();
		}, 550); // 550ms debounce

		delayFetch();
		
		return () => delayFetch.cancel();

		// eslint-disable-next-line
	}, [page, filters]);

  const fetchMasters = async () => {
    try {
      const movieResponse = await fetchMiniList("movie/select");
      movieResponse.status ? setMovies(movieResponse.data) : message.error(movieResponse.data);

      const timeResponse = await fetchMiniList("core/timeslot/");
      timeResponse.status ? setTimes(timeResponse.data) : message.error(timeResponse.data);

      const theaterResponse = await fetchMiniList("theatre-management/select");
      theaterResponse.status ? setTheaters(theaterResponse.data) : message.error(theaterResponse.data);

    } catch (error) {
      console.error("Error in fetchMasters:", error);
      message.error("An unexpected error occurred while fetching data");
    }
  };

    const cancel = () => message.info('Cancelled');

	const confirm = async (id) => {
    try {
      await axiosInstance.delete(`booking/delete-booking/${id}/`);
      message.success("Deleted successfully");
      fetchData();
    } catch (err) {
      message.error("Cannot delete");
    }
  };

  const columns = [
    {
      title: 'Movie',
      key: 'movie',
      render: (_, record) => record.show.movie,
    },
    {
      title: 'Theatre',
      key: 'theatre',
      render: (_, record) => record.show.theatre,
    },
    {
      title: 'Screen',
      key: 'screen',
      render: (_, record) => record.show.screen,
    },
    {
      title: 'Show Date & Time',
      key: 'showtime',
      render: (_, record) =>
        `${record.show_time_slot.date} - ${record.show_time_slot.title}`,
    },
    {
      title: 'Booked at',
      key: 'created_at',
      render: (_, record) => dateFormatmicroSecond(record.created_at),
    },
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) =>
        record.booked_by?.email?.trim()
          ? record.booked_by?.email
          : (record.booked_by_email?.trim() ? record.booked_by_email : "Anonymous"),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, record) => `${record.amount_to_pay}`,
    },
    {
      title: 'Reserved',
      key: 'is_reserved',
      render: (_, record) => record.is_reserved === true ? <Tag color="red">Reserved</Tag>  : <Tag color="green">'Booked'</Tag>
    },
    {
      title: 'Seats (section - block - row - position - number)',
      key: 'seats',
      render: (_, record) =>
      record.seats.length > 0 ? (
        <ul style={{ paddingLeft: 16, margin: 0 }}>
          {record.seats.map(seat => (
            <li key={seat.id}>
              {seat.section} - {seat.block} - {seat.row} - {seat.position} - {seat.number}
            </li>
          ))}
        </ul>
      ) : (
        `Order #${record.id}`
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => <Popconfirm
                  title="Delete"
                  description="Deleting booking will make seat slot available for this booking also remove from report calculation, are you sure want to delete this ?"
                  onConfirm={() => confirm(record.id)}
                  onCancel={cancel}
                  okText="Yes"
                  cancelText="No"
                >
                   <Button type="link" danger icon={<DeleteOutlined />} />
                </Popconfirm>
    }
  ];


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);

      if (filters.id) params.append("id__icontains", filters.id);
      if (filters.movie) params.append("show__movie__name__icontains", filters.movie);
      if (filters.theatre) params.append("show__theatre__name__icontains", filters.theatre);
      if (filters.timeslot) params.append("show_time_slot__timeslot__title__icontains", filters.timeslot);
      if (filters.startDate) params.append("show_date__date__gte", filters.startDate);
      if (filters.endDate) params.append("show_date__date__lte", filters.endDate);

      const response = await axiosInstance.get(`booking/completed-bookings?${params.toString()}`);
      setBookings(response.data);
    } catch (error) {
      message.error("Failed to fetch shows");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='dashboard'>
		  <PageHeading name="Completed Bookings" icon={<MoneyCollectOutlined/>} />
         <div className="filters">
        <Input
          placeholder="Booking ID"
          style={{ width: 150, margin: "3px" }}
          value={filters.id}
          onChange={(e) => setFilters(prev => ({ ...prev, id: e.target.value }))}
          allowClear
        />&nbsp;
        <Select
          showSearch
          placeholder="Movies"
          style={{ width: 180, margin: "3px" }}
          value={filters.movie}
          onChange={(value) => setFilters(prev => ({ ...prev, movie: value }))}
          options={movies.map(m => ({ label: `${m.name} (${m.language})`, value: m.name }))}
          allowClear
        />&nbsp;
        <Select 
          showSearch
          placeholder="Theatre"
          style={{ width: 160, margin: "3px"  }}
          value={filters.theatre}
          onChange={(value) => setFilters(prev => ({ ...prev, theatre: value }))}
          options={theaters.map(t => ({ label: t.name, value: t.name }))}
          allowClear
        />&nbsp;
        <Select 
          placeholder="TimeSlots"
          style={{ width: 150, margin: "3px"  }}
          value={filters.timeslot}
          onChange={(value) => setFilters(prev => ({ ...prev, timeslot: value }))}
          options={times.map(t => ({ label: t.start_time, value: t.title }))}
          allowClear
        />&nbsp;
        <RangePicker
          style={{ margin: "3px" }}
          onChange={(dates) => {
            if (dates) {
              setFilters(prev => ({ ...prev, startDate: dates[0].format("YYYY-MM-DD"), endDate: dates[1].format("YYYY-MM-DD") }));
            } else {
              setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
            }
          }}
        />&nbsp;
        <Button onClick={() => setFilters({ id: null, movie: null, theatre: null, timeslot: null, startDate: null, endDate: null })}>
          Clear
        </Button>
      </div>
      <Table
        dataSource={bookings.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: bookings.count,
          onChange: (newPage, _) => {
            setPage(newPage);
          }
        }}
      />
    </div>
  )
}

export default Bookings
